import React from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr, TreeRowWrapper } from '@patternfly/react-table';
import { MapMarkedAltIcon, SearchIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { ROADMAP, type RoadmapStatus } from './roadmapData';
import { type RoadmapRow, allGroupKeys, countItems, filterRoadmap, flattenRoadmap } from './roadmap';

function StatusLabel({ status }: { status: RoadmapStatus }) {
  return status === 'blocked' ? (
    <Label isCompact status="warning">
      Blocked
    </Label>
  ) : (
    <Label isCompact color="blue">
      Planned
    </Label>
  );
}

/** A source/section/area group row carries the expand chevron; only depth, highlight and label differ. */
type GroupRow = Extract<RoadmapRow, { kind: 'source' | 'section' | 'area' }>;

function Roadmap() {
  const auth = useAuth();

  const [searchValue, setSearchValue] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');
  // Source/section/area keys currently expanded. Seeded to every group so the tree opens fully expanded.
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(() => new Set(allGroupKeys(ROADMAP)));

  React.useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchValue), 400);
    return () => clearTimeout(handle);
  }, [searchValue]);

  const totalItems = React.useMemo(() => countItems(ROADMAP), []);
  // While a search is active, every group is force-expanded so matches are always visible.
  const isSearching = debouncedSearch.trim() !== '';
  const filtered = React.useMemo(() => filterRoadmap(ROADMAP, debouncedSearch), [debouncedSearch]);
  const rows = React.useMemo(
    () => flattenRoadmap(filtered, { isExpanded: (key) => expandedKeys.has(key), forceExpanded: isSearching }),
    [filtered, expandedKeys, isSearching],
  );

  const toggle = React.useCallback((key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const clearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
  };

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const renderGroupRow = (
    row: GroupRow,
    idx: number,
    opts: { level: 1 | 2 | 3; className?: string; toggleLabel: string; showCount: boolean },
  ): React.ReactNode => {
    const props = {
      isExpanded: row.expanded,
      isHidden: false,
      'aria-level': opts.level,
      'aria-posinset': row.posInSet,
      'aria-setsize': row.childCount,
      toggleAriaLabel: opts.toggleLabel,
    };
    return (
      <TreeRowWrapper key={row.key} row={{ props }} className={opts.className}>
        <Td dataLabel="Feature" treeRow={{ onCollapse: () => toggle(row.key), props, rowIndex: idx }}>
          <strong>{row.label}</strong>
          {opts.showCount && (
            <>
              {' '}
              <span className="pf-v6-u-font-size-sm">
                ({row.itemTotal} {row.itemTotal === 1 ? 'item' : 'items'})
              </span>
            </>
          )}
        </Td>
        <Td dataLabel="Status" />
        <Td dataLabel="Description" />
      </TreeRowWrapper>
    );
  };

  const renderRow = (row: RoadmapRow, idx: number): React.ReactNode => {
    switch (row.kind) {
      case 'source':
        return renderGroupRow(row, idx, {
          level: 1,
          className: 'ancalagon-roadmap-source-header',
          toggleLabel: `Toggle ${row.label} backlog`,
          showCount: true,
        });
      case 'section':
        return renderGroupRow(row, idx, { level: 2, toggleLabel: `Toggle ${row.label} section`, showCount: false });
      case 'area':
        return renderGroupRow(row, idx, { level: 3, toggleLabel: `Toggle ${row.label} group`, showCount: true });
      default: {
        const { item } = row;
        const props = {
          isExpanded: false,
          isHidden: false,
          'aria-level': 4,
          'aria-posinset': row.posInSet,
          'aria-setsize': 0,
        };
        return (
          <TreeRowWrapper key={row.key} row={{ props }}>
            <Td dataLabel="Feature" treeRow={{ onCollapse: () => undefined, props, rowIndex: idx }}>
              <Label isCompact color="grey">
                #{item.id}
              </Label>{' '}
              {item.title}
            </Td>
            <Td dataLabel="Status">
              <StatusLabel status={item.status} />
            </Td>
            <Td dataLabel="Description">{item.description}</Td>
          </TreeRowWrapper>
        );
      }
    }
  };

  let body: React.ReactNode;
  if (totalItems === 0) {
    body = (
      <EmptyState titleText="Coming soon" icon={MapMarkedAltIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>The product roadmap is not yet available.</EmptyStateBody>
      </EmptyState>
    );
  } else if (filtered.length === 0) {
    body = (
      <EmptyState titleText="No roadmap items match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
        <EmptyStateBody>Try a different search term or clear the filter.</EmptyStateBody>
        <EmptyStateFooter>
          <EmptyStateActions>
            <Button variant="primary" onClick={clearSearch}>
              Clear filter
            </Button>
          </EmptyStateActions>
        </EmptyStateFooter>
      </EmptyState>
    );
  } else {
    body = (
      <Table isTreeTable aria-label="Product roadmap" variant="compact">
        <Thead>
          <Tr>
            <Th>Feature</Th>
            <Th>Status</Th>
            <Th>Description</Th>
          </Tr>
        </Thead>
        <Tbody>{rows.map((row, idx) => renderRow(row, idx))}</Tbody>
      </Table>
    );
  }

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem isActive>Roadmap</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Content component="h1">
          <Icon size="lg" style={{ marginRight: '8px' }}>
            <MapMarkedAltIcon />
          </Icon>
          Roadmap
        </Content>
        <Content component="p">
          Planned and in-progress work across the Frontend and Backend, grouped by feature area
        </Content>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        {totalItems > 0 && (
          <Toolbar id="roadmap-toolbar">
            <ToolbarContent>
              <ToolbarItem>
                <SearchInput
                  placeholder="Search the roadmap..."
                  value={searchValue}
                  onChange={(_event, value) => setSearchValue(value)}
                  onClear={clearSearch}
                  aria-label="Search roadmap items"
                />
              </ToolbarItem>
            </ToolbarContent>
          </Toolbar>
        )}
        {body}
      </PageSection>
    </>
  );
}

export { Roadmap };
